/**
 * ARENA PVE — BẢN DỊCH TIẾNG ANH.
 *
 * VÌ SAO TÁCH KHỎI `content-en.ts`: file đó là bảng dịch của game TD 10-wave (class/ chiêu/ màn/
 * quái/ bình thuốc) và `tests/i18n.test.ts` quét CẢ file để khoá chất lượng. Arena là chế độ BỔ
 * SUNG, nội dung của nó không nên nằm lẫn trong bảng của game cũ — thêm vào đó là làm nhiễu phạm vi
 * của một file đã có test phủ, và mọi lần Arena đổi chữ lại phải chạy lại test của game cũ.
 *
 * ⚠️⚠️ 3 QUY ƯỚC CỦA REPO — VI PHẠM LÀ `tests/i18n.test.ts` (và test của task này) ĐỎ:
 *   1. Bản EN **KHÔNG được chứa ký tự có dấu tiếng Việt** (à á ả ã ạ ă â đ ê ô ơ ư è é … ỳ ý ỷ ỹ ỵ).
 *      Viết không dấu vẫn ĐỌC ĐƯỢC ("Nong dan" đọc như tiếng Việt không dấu) nhưng đó KHÔNG phải
 *      tiếng Anh. Vì vậy nội dung thật sự phải được DỊCH, không phải bỏ dấu.
 *   2. Bản EN **phải GIỮ NGUYÊN mọi con số** của bản VI. Người chơi đọc "Lính mỗi 20 giây" mà bản
 *      Anh ghi "every 30s" là đọc SAI luật chơi — đây là lỗi nặng nhất trong bản dịch, đúng như
 *      `tests/i18n.test.ts` đã khoá cho bản dịch chiêu.
 *   3. Bản VI KHÔNG chép sang đây. Nguồn sự thật của chữ Việt là `data/arena.ts` (`ARENA_TEXT`,
 *      `ARENA_LAND_NAME`, `arenaUnitName()`); file này tra theo **ID**.
 *
 * ⚠️ VÌ SAO KHÔNG ĐẶT CÁC CHUỖI NÀY VÀO `ARENA_TEXT` Ở `data/arena.ts`: `content-en.ts` tồn tại
 * đúng để tách bản dịch khỏi dữ liệu — một bảng `ARENA_TEXT` hai thứ tiếng sẽ lặp lại sai lầm mà cả
 * hệ i18n của repo đang tránh (nội dung gốc là VI, bản EN tra theo id).
 */
import { ARENA_TEXT, type ArenaTextKey } from '../data/arena';

/** Hình dạng bản dịch — mọi khoá của `ARENA_TEXT` đều bắt buộc (xem chú thích `ARENA_TEXT_EN`). */
export type ArenaTextEn = Readonly<Record<ArenaTextKey, string>>;

/**
 * BẢN DỊCH EN THEO **ID**, phủ HẾT `ARENA_TEXT`.
 *
 * ⚠️⚠️ VÌ SAO DÙNG `Record<ArenaTextKey, string>` CHỨ KHÔNG PHẢI `Record<string, string>`:
 * `ArenaTextKey` suy từ chính `ARENA_TEXT`, nên thêm một khoá mới ở bảng VI mà quên dịch ⇒
 * **`npm run typecheck` đỏ ngay tại đây**. Với `Record<string, string>`, thiếu khoá chỉ lộ ra khi
 * người chơi tiếng Anh nhìn thấy chữ tiếng Việt giữa giao diện — đúng loại lỗi khó phát hiện nhất.
 * Đây là lý do `content-en.ts` của game cũ phải giữ test quét phủ; ở đây TypeScript làm việc đó.
 *
 * ⚠️ Số trong câu PHẢI khớp bản VI: `20`/ `60`/ `180`/ `300` (nhịp người dùng chốt) và `120`/ `5`
 * trong 2 câu mẫu. `tests/arena-data.test.ts` đối chiếu từng cặp bằng cùng cách chuẩn hoá số của
 * `tests/i18n.test.ts`.
 */
export const ARENA_TEXT_EN: ArenaTextEn = {
  /*
   * ⚠️ 3 khoá `landTop`/`landMiddle`/`landBot` ĐÃ ĐƯỢC XOÁ ĐỒNG THỜI VỚI BẢNG VI (`ARENA_TEXT`).
   * Luật chơi không còn "3 land" (sân chia bởi SÔNG + 2 CẦU), nên không còn dải nào để đặt tên. Hai
   * bảng phải phủ ĐÚNG cùng tập khoá — xoá một bên là `typecheck` đỏ, nên chúng được xoá cùng lúc
   * (kèm `ARENA_LAND_NAME_EN` bên dưới và các assertion trong `tests/arena-data.test.ts`).
   */
  sideLeft: 'Your Side',
  sideRight: 'Enemy Side',
  core: 'Home Base',
  // ── tài nguyên + kinh tế
  resource: 'Resource',
  mine: 'Resource Mine',
  farmer: 'Farmer',
  buyFarmer: 'Buy Farmer',
  carrying: 'Carrying',
  // ── summon
  summon: 'Summon',
  cost: 'Cost',
  // ── đồng hồ trận + kết quả
  matchTime: 'Time',
  score: 'Score',
  statusPrep: 'Prep',
  statusFighting: 'Fighting',
  statusWon: 'Victory',
  statusLost: 'Defeat',
  statusDraw: 'Draw',
  // ── nhịp thả quân: SỐ KHÔNG ĐỔI so với bản VI
  waveEvery: 'Minions every 20 seconds',
  bossEvery: 'Elise every 60 seconds',
  titanEvery: 'Titan every 180 seconds',
  matchLength: 'Match lasts 300 seconds',
  /*
   * ── MẪU CÂU UI.
   * ⚠️ Đây là MẪU của tầng UI, KHÔNG phải bản dịch message của `sim/arena/world.ts` (xem chú thích
   * `ARENA_TEXT`). Message thật của sim đi qua `simMsg()` — bảng mẫu regex trong
   * `i18n/sim-messages.ts`. Đừng gộp hai đường này làm một: sim trả message tiếng Việt có test riêng
   * khoá nguyên văn, còn UI tự dựng câu từ số liệu.
   */
  msgNotEnough: 'Not enough resource (need 120)',
  msgClassLocked: 'Gym Rat is still locked',
  msgFieldFull: 'The field already has 5 heroes',
  msgMatchOver: 'The match has ended',
  /*
   * ⚠️ `msgHeroDeployed` ĐÃ XOÁ CÙNG BẢNG VI: mẫu câu của API CŨ `summonHero(classId, land)`. Nay
   * `summonHero(classId, x, y)` và message thật do sim sinh ra, dịch qua `simMsg()` — không qua bảng này.
   */
};

/*
 * ⚠️ `ARENA_LAND_NAME_EN` ĐÃ BỊ XOÁ: nó là mảng 3 tên land suy từ 3 khoá vừa xoá, và không còn ai
 * đọc (HUD không còn nhãn land, lớp vẽ không còn 3 dải). Giữ lại chỉ là một nguồn chữ thứ hai.
 */

/**
 * TÊN ĐƠN VỊ ARENA TIẾNG ANH theo `kind`.
 *
 * ⚠️⚠️ VÌ SAO KHÔNG DỊCH `kind = 'thug'` Ở ĐÂY: lính của Arena dùng CHUNG id `thug` với quái của
 * game TD, và tên EN của nó đã nằm trong `ENEMY_EN` của `content-en.ts` (`'Neighborhood Thug'`).
 * Khai lại ở đây là hai bản dịch cho cùng một id — sửa một bên, bên kia lệch, và test phủ của
 * `tests/i18n.test.ts` chỉ kiểm bên `content-en.ts` nên sẽ KHÔNG bắt được. Tầng hiển thị hãy gọi
 * `enemyName(kind)` (`i18n/loc.ts`) cho mọi kind — nó đã lùi về chính `kind` khi chưa có bản dịch.
 *
 * Bảng dưới chỉ chứa 2 id RIÊNG của Arena (không có trong `ENEMIES`) cộng nông dân — đúng những id
 * mà `ARENA_UNIT_DEF`/ `ARENA_UNIT_NAME` bên `sim/arena/world.ts` khai.
 */
export const ARENA_UNIT_NAME_EN: Record<string, string> = {
  elise: 'Elise',
  titan: 'Titan',
  farmer: 'Farmer',
};

/** TÊN BOSS ARENA — hằng số để nơi gọi không phải nhớ khoá. Trùng `ARENA_BOSS` của sim. */
export const ARENA_BOSS_NAME_EN = 'Elise';
